import React from 'react';
import { motion } from 'framer-motion';
import { Check, Wrench, Search, Zap, Cpu, AlertTriangle, FileText, ShieldCheck, BadgeCheck, ClipboardCheck, Handshake, ShieldAlert } from 'lucide-react';
export function MaintenanceServices() {
  const inspectionItems = ['Airframe inspection', 'Engine inspection', 'Propeller inspection', 'Avionics operational check', 'Flight control inspection', 'Fuel system inspection', 'Electrical system check', 'Interior and exterior inspection', 'Control surface check', 'Safety and compliance review', 'Lubrication as required', 'Light system inspection', 'Pilot and static system check', 'ELT operational check', 'Placards and markings check', 'Weight and balance review', 'Detailed logbook entry', 'Discrepancy review', 'Return to service approval'];
  const lineServices = [{
    icon: Wrench,
    title: 'A&P Certified Line Maintenance'
  }, {
    icon: Search,
    title: 'Pre and Post Flight Inspections'
  }, {
    icon: Zap,
    title: 'Engines and APU Services'
  }, {
    icon: Cpu,
    title: 'Avionics Support'
  }, {
    icon: AlertTriangle,
    title: 'Discrepancy Troubleshooting'
  }, {
    icon: FileText,
    title: 'Logbook Entry and Return to Service'
  }];
  return <section className="py-24 bg-[#0d1323] relative border-b border-white/5" data-id="element-304">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-305">
        <div className="text-center mb-12" data-id="element-306">
          <span className="text-gold text-sm font-bold tracking-widest uppercase mb-3 block" data-id="element-307">
            A&P Services
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4 uppercase" data-id="element-308">
            Certified. Experienced. Dependable.
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto" data-id="element-309">
            Professional line maintenance services that keep your aircraft safe,
            compliant and ready to fly.
          </p>
        </div>

        {/* Highlights */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-16" data-id="element-310">
          {['A&P Certified Mechanics', '24/7 Availability', 'Safety Focused', 'Quality Workmanship'].map((badge, idx) => <div key={idx} className="bg-navy border border-white/10 rounded-full px-6 py-2 flex items-center" data-id="element-311">
              <ShieldCheck className="w-4 h-4 text-gold mr-2" data-id="element-312" />
              <span className="text-sm font-medium text-slate-300 uppercase tracking-wider" data-id="element-313">
                {badge}
              </span>
            </div>)}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8" data-id="element-314">
          {/* Left Column: 100 Hour */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-navy rounded-2xl p-8 border border-white/5" data-id="element-315">
            <h3 className="text-2xl font-heading font-bold text-white mb-2 uppercase" data-id="element-316">
              100 Hour Inspections
            </h3>
            <p className="text-slate-400 text-sm mb-8 pb-6 border-b border-white/10" data-id="element-317">
              On Smaller Airplanes. Performed in accordance with FAR 43 Appendix
              D and manufacturer guidelines.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6" data-id="element-318">
              {inspectionItems.map((item, idx) => <div key={idx} className="flex items-start" data-id="element-319">
                  <Check className="w-5 h-5 text-gold mr-3 shrink-0 mt-0.5" data-id="element-320" />
                  <span className="text-slate-300 text-sm" data-id="element-321">{item}</span>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column: Line Maintenance */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="flex flex-col gap-4" data-id="element-322">
            <div className="bg-navy rounded-2xl p-8 border border-white/5 mb-2" data-id="element-323">
              <h3 className="text-2xl font-heading font-bold text-white uppercase" data-id="element-324">
                Line Maintenance Services
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-grow" data-id="element-325">
              {lineServices.map((service, idx) => <div key={idx} className="bg-navy rounded-2xl p-6 border border-white/5 hover:border-gold/30 transition-colors flex flex-col items-center text-center justify-center group" data-id="element-326">
                  <div className="w-12 h-12 rounded-full bg-navy-light flex items-center justify-center mb-4 group-hover:shadow-gold-glow transition-all" data-id="element-327">
                    <service.icon className="w-6 h-6 text-gold" data-id="element-328" />
                  </div>
                  <h4 className="text-white font-heading font-medium tracking-wide uppercase text-sm" data-id="element-329">
                    {service.title}
                  </h4>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Bottom Commitments */}
        <div className="mt-16 pt-12 border-t border-white/5 grid grid-cols-2 md:grid-cols-4 gap-6 text-center" data-id="element-330">
          {[{
          icon: BadgeCheck,
          text: 'Certified A&P Professionals'
        }, {
          icon: ClipboardCheck,
          text: 'FAA Compliant'
        }, {
          icon: Handshake,
          text: 'Transparent & Honest'
        }, {
          icon: ShieldAlert,
          text: 'Safety Is Our Priority'
        }].map((item, idx) => <div key={idx} className="flex flex-col items-center" data-id="element-331">
              <div className="w-10 h-10 rounded-full bg-navy border border-gold/30 flex items-center justify-center mb-3" data-id="element-332">
                <item.icon className="w-5 h-5 text-gold" data-id="element-333" />
              </div>
              <span className="text-slate-400 text-sm font-medium uppercase tracking-wider" data-id="element-334">
                {item.text}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
}